import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { microsoftAuthEnabled, microsoftLoginPopup } from '../lib/microsoftAuth';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';

export default function Login() {
  const navigate = useNavigate();
  const { login, loginWithMicrosoftToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Login realizado com sucesso!');
      navigate('/');
    } catch (error) {
      const timeoutMessage = 'Servidor indisponível. Tente novamente em instantes.';
      toast.error(error.code === 'ECONNABORTED' ? timeoutMessage : error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    try {
      const idToken = await microsoftLoginPopup();
      await loginWithMicrosoftToken(idToken);
      toast.success('Login Microsoft realizado com sucesso!');
      navigate('/');
    } catch (error) {
      const timeoutMessage = 'Servidor indisponível. Tente novamente em instantes.';
      toast.error(error.code === 'ECONNABORTED' ? timeoutMessage : error.response?.data?.detail || error.message || 'Erro ao fazer login com Microsoft');
    } finally {
      setMicrosoftLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {SHOW_INSTITUTO_BW_BRANDING && (
            <div className="inline-flex items-center justify-center mb-4">
              <img
                src="/bw-symbol-final.png"
                alt="Instituto BW"
                className="h-32 w-32 object-contain"
              />
            </div>
          )}
          <h1 className="text-3xl font-bold text-emerald-800 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Sistema CRAS</h1>
          <p className="text-emerald-600 mb-1">Centro de Reabilitação de Animais Silvestres</p>
          {SHOW_INSTITUTO_BW_BRANDING && (
            <p className="text-sm text-emerald-500 font-medium">Instituto BW</p>
          )}
        </div>

        <Card className="shadow-lg border-emerald-100">
          <CardHeader>
            <CardTitle>Entrar</CardTitle>
            <CardDescription>Acesse sua conta do sistema</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="seu@email.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="login-password">Senha</Label>
                <Input
                  id="login-password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="login-submit-button"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>
            {microsoftAuthEnabled && (
              <>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-emerald-100" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-emerald-500">ou</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
                  disabled={microsoftLoading}
                  onClick={handleMicrosoftLogin}
                >
                  {microsoftLoading ? 'Conectando com Microsoft...' : 'Entrar com Microsoft'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
